import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CardHeader, Dialog, DialogActions, DialogTitle, FormControlLabel, FormGroup, IconButton, Input, InputAdornment, Switch, Tooltip, Checkbox } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import DataTable from "react-data-table-component";
import { capitalize } from "../../utils/helpers";
import errorHandler from "../../utils/errorHandler";
import { toast } from "react-toastify";
import { EditOutlined } from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import Swal from "sweetalert2";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Helmet } from "react-helmet-async";
import { fDateTime } from "../../utils/formatTime";
import { apiOfferBannerManagement } from "../../services/AllServices";
import CustomLoader from "../../components/CustomLoader";
import CustomPagination from "../../components/CustomPagination";
import { useBulkSelect } from "../../hooks/useBulkSelect";

export default function Offerandbannermanagement() {
  const navigate = useNavigate();

  const [searchItem, setSearchItem] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [loading, setLoading] = useState(false);

  const {
    selectedRows,
    getCheckboxColumn,
    clearSelection,
  } = useBulkSelect(data, null, [searchItem, currentPage, pageSize, refresh, status]);

  const handleClose = () => setShow(false);

  // Helper function to check if delete/status should be disabled
  const shouldDisableAction = (row) => {
    const totalBanners = data.length;
    const activeBanners = data.filter(banner => banner.status === 1).length;
    
    // If only one banner exists, disable delete and status
    if (totalBanners === 1) {
      return true;
    }
    
    // If only one active banner exists and this is that active banner, disable delete and status
    if (activeBanners === 1 && row.status === 1) {
      return true;
    }
    
    return false;
  };

  // Helper function to check if edit should be disabled based on start date
  const shouldDisableEdit = (row) => {
    // Check if start date exists
    if (!row?.visibilityPeriod?.startDate) {
      return false; // Allow edit if no start date is set
    }
    
    // Convert start date to Date object and compare with current date/time
    const startDate = new Date(row.visibilityPeriod.startDate);
    const currentDate = new Date();
    
    // If start date is in the past or current, disable editing
    // Only allow editing if start date is in the future
    return startDate <= currentDate;
  };

  const columns = [
    getCheckboxColumn(),
    {
      name: "S.No.",
      sortable: false,
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => capitalize(row?.title),
    },
    {
      name: "Banner Type",
      sortable: true,
      selector: (row) => capitalize(row?.bannerType),
    },
    {
      name: "Auction",
      sortable: true,
      selector: (row) => row?.auctionId ? capitalize(row?.auctionId?.vehicle?.make?.name + " " + row?.auctionId?.vehicle?.model) : "",
    },
    {
      name: "Auction Status",
      sortable: true,
      selector: (row) => capitalize(row?.auctionId?.auctionStatus),
    },
    {
      name: "Start Date",
      sortable: true,
      selector: (row) => fDateTime(row?.visibilityPeriod?.startDate),
    },
    {
      name: "End Date",
      sortable: true,
      selector: (row) => fDateTime(row?.visibilityPeriod?.endDate),
    }, 
    {
      name: "Status",
      cell: (row) => {
        const isDisabled = shouldDisableAction(row);
        return (
          <>
            <FormGroup>
              <FormControlLabel
                control={
                  <Switch
                    checked={row.status === 1}
                    disabled={isDisabled}
                    onClick={() => {
                      if (!isDisabled) {
                        setShow(true);
                        setCurrentStatus(row.status);
                        setStatusId(row._id);
                        setAction("status");
                        setWarning(
                          `Are you sure you want to ${
                            row.status === 1 ? "deactive" : "activate"
                          } this offer/banner?`
                        );
                      }
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                  />
                }
              />
            </FormGroup>
          </>
        );
      },
    },  
    {
      name: "Action",
      right: true,
      cell: (row) => {
        const isDisabled = shouldDisableAction(row);
        return (
          <>
            <Tooltip title="View" placement="top">
              <IconButton
                color="primary"
                onClick={() => {
                  navigate(`/banner-management/view/${row?._id}`);
                }}
              >
                <VisibilityIcon />
              </IconButton>
            </Tooltip>
            <Tooltip 
              // title={
              //   shouldDisableEdit(row) 
              //     ? "Cannot edit. Banner start date has passed." 
              //     : "Edit"
              // } 
              title={
                "Edit"
              } 
              placement="left"
            >
              <IconButton
                color="primary"
                // disabled={shouldDisableEdit(row)}
                onClick={() => {
                  navigate(`/banner-management/edit/${row?._id}`);
                }}
              >
                <EditOutlined />
              </IconButton>
            </Tooltip>
            <Tooltip title={isDisabled ? "Cannot delete. At least one banner must remain active." : "Delete"} placement="right">
              <span>
                <IconButton 
                  color="primary" 
                  disabled={isDisabled}
                  onClick={() => {
                    if (!isDisabled) {
                      deleteData(row?._id);
                    }
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        );
      },
    },
  ];

  const fetchProductData = () => {
    const body = {
      search: searchItem ? searchItem.trim() : "",
      pageNumber: currentPage,
      pageSize: pageSize,
      status: status,
    };

    apiOfferBannerManagement
      .getAll(body)
      .then((res) => {
        if (res.status === 200) {
          const responseData = res?.data?.data;
          setData(responseData?.banners || []);
          setTotalRecords(responseData?.total || 0);
        }
      })
      .catch((err) => {
        errorHandler(err);
      })
      .finally(() => {
        setLoading(false);
      })
  };

  useEffect(() => {
    setLoading(true);
    fetchProductData();
  }, [searchItem, currentPage, pageSize, refresh, status]);

  const deleteData = async (ids) => {
    const isMultiple = Array.isArray(ids);
    Swal.fire({
      text: isMultiple 
        ? `Are you sure you want to delete ${ids.length} selected banner(s)?`
        : "Are you sure you want to delete this Banner?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const bannerIds = isMultiple ? ids : [ids];
          const payload = bannerIds.length === 1 
            ? { id: bannerIds[0] }
            : { id: bannerIds };
          
          const res = await apiOfferBannerManagement.deleteBanner(payload);
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            clearSelection();
            setRefresh(!refresh);
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const handleBulkDelete = () => {
    if (selectedRows.length > 0) {
      // Check if deleting selected banners would leave no active banners
      const activeBanners = data.filter(banner => banner.status === 1);
      const selectedActiveBanners = data.filter(banner => 
        selectedRows.includes(banner._id) && banner.status === 1
      );
      
      // If all active banners are selected for deletion, prevent it
      if (activeBanners.length === selectedActiveBanners.length && activeBanners.length > 0) {
        toast.error("Cannot delete. At least one active banner must remain.", { toastId: "error" });
        return;
      }
      
      // If deleting all banners, prevent it
      if (selectedRows.length === data.length) {
        toast.error("Cannot delete all banners. At least one banner must remain.", { toastId: "error" });
        return;
      }
      
      deleteData(selectedRows);
    }
  };

  const toggleChecked = async (id, currentStatus) => {
    try {
      const newStatus = currentStatus ? 0 : 1;
      
      // Prevent deactivating if this is the last active banner
      if (newStatus === 0) {
        const activeBanners = data.filter(banner => banner.status === 1);
        if (activeBanners.length === 1 && activeBanners[0]._id === id) {
          toast.error("Cannot deactivate. At least one active banner must remain.", { toastId: "error" });
          setShow(false);
          return;
        }
      }
      
      const res = await apiOfferBannerManagement.changeStatus({ id, status: newStatus });
      if (res?.status === 200) {
        toast.success(res?.data?.message, { toastId: "success" });
        setRefresh(!refresh);
        setShow(false);
      } else {
        toast.error(res?.data?.message, { toastId: "error" });
        setShow(false);
      }
    } catch (err) {
      setShow(false);
      errorHandler(err);
    }
  };

  return (
    <Card>
      <Helmet>
        <title>Banner Management</title>
      </Helmet>
      <CardHeader title="Banner Management" titleTypographyProps={{ variant: "h4" }} />
      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <Input
          placeholder="Search"
          value={searchItem}
          onChange={(e) => setSearchItem(e.target.value.trimStart())}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setSearchItem("")}>
                <CloseIcon />
              </IconButton>
            </InputAdornment>
          }
        />
        {selectedRows.length > 0 && (
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            startIcon={<DeleteIcon />}
            sx={{ ml: 3, mr: 3 }}
          >
            Delete Selected ({selectedRows.length})
          </Button>
        )}
        <Button
          size="large"
          variant="contained"
          sx={{ ml: 3, mr: 3 }}
          onClick={() => navigate("/banner-management/add")}
        >
          Add
        </Button>
      </Box>
      <CardContent>
        <DataTable
         customStyles={{
          headCells: {
            style: {
              fontSize: "15px",
              fontWeight: "bold",
            },
          },
        }}
          columns={columns}
          data={data}
          pagination
          paginationServer
          paginationTotalRows={totalRecords}
          onChangePage={(page) => setCurrentPage(page)}
          paginationPerPage={pageSize}
          paginationRowsPerPageOptions={[5, 10, 20, 50]}
          onChangeRowsPerPage={(currentRowsPerPage) => {
            setPageSize(currentRowsPerPage);
            setCurrentPage(1);
          }}
          paginationComponent={(props) => (
            <CustomPagination {...props} currentPage={currentPage} />
          )}
          progressPending={loading}
          progressComponent={<CustomLoader />}
        />
      </CardContent>
      <Dialog open={show} onClose={handleClose}>
        <DialogTitle>{warning}</DialogTitle>
        <DialogActions>
          <Button variant="outlined" onClick={() => toggleChecked(statusId, currentStatus)}>
            Yes
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}